import {IPriceBand, IPriceBandSnap} from '../../shared-models/price-banding/price-band';
import {getDeepCopyOfObject, isNonEmptyObject} from '../object/object.utils';
import {convertToDecimal, isValueBetweenRanges, splitValueAtN} from '../number/number.utils';
import {calculateGPFromPrice} from '../calculations/calculations-stock';
import {IStore} from '../../shared-models/store/store';
import {VatRates} from '../../../shared-utilities/models-old/datastructures';

export const PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS: string[] = [
  'diffGP',
  'nominalGP',
  'sellPriIncl1',
];

export function applyPriceBanding<ObjectType>(
  obj: ObjectType,
  priceBands: IPriceBand[],
  cols: string[],
  store: IStore,
  vatRates: VatRates,
): ObjectType {
  if (Array.isArray(obj)) {
    return obj.map((item: ObjectType) => {
      if (item?.['_tags']?.includes('price_banding_exception')) {
        return item;
      }
      const original = item['originalValue'];
      const isModified = cols.some(key => original?.[key]?.value !== item[key]);
      return isModified ? applyPriceBandingValues(item, priceBands, store, vatRates) : item;
    }) as ObjectType;
  }
  if (obj?.['_tags']?.includes('price_banding_exception')) {
    return obj;
  }
  const copy = getDeepCopyOfObject<ObjectType>(obj);
  const original = copy['originalValue'];

  const isModified = original && cols.some(key => original[key]?.value !== copy[key]);
  return isModified ? applyPriceBandingValues(copy, priceBands, store, vatRates) : copy;
}

function applyPriceBandingValues<ObjectType>(
  obj: ObjectType,
  priceBands: IPriceBand[],
  store: IStore,
  vatRates: VatRates,
): ObjectType {
  const price = +obj['sellPriIncl1'];
  const dep = obj['dep'] as string;
  const subDep = obj['subDep'] as string;

  if (price === 0) {
    return calculateGPFromPrice(obj, store, 2, vatRates);
  }

  for (const band of priceBands) {
    if (!isValueBetweenRanges(price, band.range)) continue;
    return applyPriceBandingValueSnap({...obj}, band, store, vatRates);
  }

  return obj;
}

function applyPriceBandingValueSnap<ObjectType>(
  obj: ObjectType,
  band: IPriceBand,
  store: IStore,
  vatRates: VatRates,
): ObjectType {
  if (band.randSnap?.snaps?.length) {
    for (const snap of band.randSnap.snaps) {
      obj = applyPriceBandingRandSnap(obj, snap, band.randSnap.digits);
    }
  }

  if (band.centSnaps?.length) {
    for (const snap of band.centSnaps) {
      obj = applyPriceBandingCentSnap(obj, snap);
    }
  }

  return calculateGPFromPrice(obj, store, 2, vatRates);
}

function applyPriceBandingRandSnap<ObjectType>(
  obj: ObjectType,
  snap: IPriceBandSnap,
  digits: number,
): ObjectType {
  let price = +obj['sellPriIncl1'];
  const floored = Math.floor(price);
  const [head, tail] = splitValueAtN(price, digits);

  if (tail !== undefined && isValueBetweenRanges(tail, snap.range)) {
    const decimal = price !== floored ? convertToDecimal(price - floored, 0) : 0;
    price = head + snap.v + decimal;
    obj['sellPriIncl1'] = convertToDecimal(price, 2);
  }

  return obj;
}

function applyPriceBandingCentSnap<ObjectType>(
  obj: ObjectType,
  snap: IPriceBandSnap,
): ObjectType {
  let price = +obj['sellPriIncl1'];
  const floored = Math.floor(price);
  const cents = Math.round((price - floored) * 100);

  if (isValueBetweenRanges(cents, snap.range)) {
    price = (floored * 100 + snap.v) / 100;
    obj['sellPriIncl1'] = price.toFixed(2);
  }

  return obj;
}

/**
 * Converts a Firestore price-band document (index-keyed record or array) into a stable array.
 */
export function normalizePriceBandsDocument(
  data: Record<number, IPriceBand> | Record<string, IPriceBand> | IPriceBand[] | null | undefined,
): IPriceBand[] {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data.filter((band: IPriceBand | undefined): band is IPriceBand => isPriceBandLike(band));
  }
  if (!isNonEmptyObject(data)) {
    return [];
  }

  const nested: unknown = data['priceBands'] ?? data['price_bands'] ?? data['bands'];
  if (nested && nested !== data) {
    return normalizePriceBandsDocument(nested as Record<number, IPriceBand> | IPriceBand[]);
  }

  const numericBands: IPriceBand[] = Object.keys(data)
    .filter((key: string): boolean => !Number.isNaN(Number(key)))
    .sort((left: string, right: string): number => Number(left) - Number(right))
    .map((key: string): IPriceBand | undefined => data[key])
    .filter((band: IPriceBand | undefined): band is IPriceBand => isPriceBandLike(band));
  if (numericBands.length) {
    return numericBands;
  }

  return Object.values(data).filter((band: unknown): band is IPriceBand => isPriceBandLike(band));
}

function isPriceBandLike(value: unknown): value is IPriceBand {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const band: IPriceBand = value as IPriceBand;
  return !!(band.range || band.centSnaps || band.randSnap || band.name);
}

/**
 * Converts an editor array back into the Firestore index-keyed document shape.
 */
export function toPriceBandsDocument(bands: IPriceBand[]): Record<number, IPriceBand> {
  const document: Record<number, IPriceBand> = {};
  bands.forEach((band: IPriceBand, index: number): void => {
    document[index] = JSON.parse(JSON.stringify(band)) as IPriceBand;
  });
  return document;
}

/**
 * Applies the first matching price-band snaps to a selling price without recalculating GP.
 */
export function snapPriceToPriceBands(price: number, priceBands: IPriceBand[] | undefined): number {
  if (!price || !Array.isArray(priceBands) || priceBands.length === 0) {
    return price;
  }

  for (const band of priceBands) {
    if (!band?.range || !isValueBetweenRanges(price, band.range)) {
      continue;
    }
    const holder: {sellPriIncl1: number | string} = {sellPriIncl1: price};
    if (band.randSnap?.snaps?.length) {
      for (const snap of band.randSnap.snaps) {
        applyPriceBandingRandSnap(holder, snap, band.randSnap.digits);
      }
    }
    if (band.centSnaps?.length) {
      for (const snap of band.centSnaps) {
        applyPriceBandingCentSnap(holder, snap);
      }
    }
    return Number(holder.sellPriIncl1);
  }

  return price;
}
